import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { VehicleTiresTab } from "../features/tires/VehicleTiresTab";
import { TabBar } from "../features/tires/tire-ui";
import { api } from "../lib/api";
import { fmtBRL, fmtPct, fmtNumber, fmtDateShort, fmtDateTime, tidyText } from "../lib/format";
import { computeDelta, type DeltaResult } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import {
  marginTone, costPerKmTone, consumptionTone, vsFleet, vehicleAttention, vehicleHeroStatus,
} from "../lib/vehicle-drilldown";
import { KpiTile, SplitMeter, DeltaChip } from "../components/intelligence/KpiTile";
import { AttentionPanel } from "../components/intelligence/AttentionPanel";
import { SystemHero, HeroNumber } from "../components/intelligence/SystemHero";
import { PageHeader } from "../components/PageHeader";
import { StatusPill } from "../components/StatusPill";
import { EmptyState } from "../components/EmptyState";
import { PageSkeleton } from "../components/LoadingSkeleton";
import {
  IconAlertTriangle, IconTruck, IconLightbulb, IconCalculator, IconDocument, IconDollar, IconInfo,
  IconX, IconChevronDown, IconChevronRight, IconTire, IconFuel, IconWrench, IconLineChart, IconDatabase,
} from "../components/icons";
import { PlateRunHistory } from "../features/maintenance/PlateRunHistory";
import { KmTimelinePanel } from "../components/KmTimelinePanel";
import type { TransportVehicleDrilldownDto, VehicleMetricMemory } from "@bi/types";
import { TollCoverageNotice, SEM_EXTRATO_TITLE } from "../components/TollCoverageNotice";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

interface VehicleCteRow { serie: string | null; numero: number; issueDate: string; customer: string | null; formattedValue: string }

interface VehicleTollPassage { id: string; occurredAt: string; description: string | null; category: string | null; amount: number; formattedAmount: string; invoiceNumber: string }
interface VehicleTollRouteGroup {
  key: string; metodo: "viagem" | "cte_data" | "sem_vinculo";
  cteSeries: string | null; cteNumber: number | null; originCity: string | null; destinationCity: string | null;
  customer: string | null; from: string | null; to: string | null;
  freightTolls: number | null; paidTolls: number; formattedPaid: string; count: number; passages: VehicleTollPassage[];
}
interface VehicleTollsMemory {
  plate: string; source: "sem_parar" | "nenhum" | "sem_extrato"; total: number; formattedTotal: string; count: number;
  freightTolls: number; groups: VehicleTollRouteGroup[]; basis: { trips: number; freights: number; ctes: number }; invoiceNumbers: string[];
}
interface VehicleCtesResponse { plate: string; count: number; formattedTotal: string; ctes: VehicleCteRow[] }

const fmt2 = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const brlKm = (v: number) => `R$ ${fmt2(v)}`;
const CAT_LABEL: Record<string, string> = {
  combustivel: "Combustível", manutencao: "Manutenção", pneus: "Pneus", pedagio: "Pedágio",
  seguro: "Seguro", ipva: "IPVA", licenciamento: "Licenciamento", financiamento: "Financiamento",
  imobilizado: "Imobilizado", rastreamento: "Rastreamento", beneficios: "Benefícios",
  salario: "Salários", administrativo: "Administrativo", frete: "Frete", multa: "Multas", outros: "Outros",
};
const catLabel = (c: string) => CAT_LABEL[c] ?? c.charAt(0).toUpperCase() + c.slice(1);

/** Mesmas cores da composição da margem na tela Margem por Placa. */
const DIRECT_COLOR: Record<string, string> = {
  combustivel: "#DAA72B", manutencao: "#E8A33D", motorista: "#8B5CF6",
  pedagio: "#14B8A6", despesas: "#EC4899", seguro: "#3B82F6",
};
const CATEGORY_COLORS = ["var(--brand)", "#3B82F6", "#F59E0B", "#14B8A6", "#A855F7", "#EF4444", "#94A3B8", "#EC4899"];

/** Valor positivo que arredondaria pra "0,0%" vira "<0,1%" (não parece zero). */
const pctFmt = (v: number) =>
  v > 0 && v < 0.05 ? "<0,1%" : `${v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** Barrinha de proporção (mesma das telas de Custo por Placa e Margem por Placa). */
function ShareBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

/** Cabeçalho de card no padrão das telas de Frota: ícone, título, subtítulo e ação à direita. */
function CardHead({ icon, title, subtitle, action }: { icon: ReactNode; title: string; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="px-5 py-4 border-b border-surface-border flex items-start gap-2">
      <span className="mt-0.5 text-gray-400 flex-shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-label="Fechar"
      className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-surface-muted hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500">
      <IconX className="w-3.5 h-3.5" /> Fechar
    </button>
  );
}

/** Aviso dentro da tela: azul para contexto, âmbar para leitura com cuidado. */
function Notice({ tone = "info", children }: { tone?: "info" | "warning"; children: ReactNode }) {
  const cls = tone === "warning"
    ? { box: "border-warning-100 bg-warning-50", icon: "text-warning-600", text: "text-warning-700" }
    : { box: "border-info-100 bg-info-50", icon: "text-info-600", text: "text-gray-700" };
  const Icon = tone === "warning" ? IconAlertTriangle : IconInfo;
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex gap-2 ${cls.box}`}>
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cls.icon}`} />
      <div className={`text-xs leading-snug ${cls.text}`}>{children}</div>
    </div>
  );
}

/** Número de uma conferência (KM, pedágio): rótulo pequeno em cima, valor grande embaixo. */
function AuditStat({ label, value, note, tone }: { label: string; value: ReactNode; note?: ReactNode; tone?: "warning" | "muted" | null }) {
  const color = tone === "warning" ? "text-warning-700" : tone === "muted" ? "text-gray-400" : "text-gray-900";
  return (
    <div className="rounded-lg bg-surface-muted px-3 py-2.5">
      <p className="text-xs text-gray-600">{label}</p>
      <p className={`font-display font-semibold text-lg tabular-nums mt-0.5 ${color}`}>{value}</p>
      {note && <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{note}</p>}
    </div>
  );
}

/** Card de KPI clicável: abre/fecha a memória de cálculo daquela métrica logo abaixo da grade. */
function MetricCardButton({ metricKey, open, onToggle, children }: {
  metricKey: string; open: string | null;
  onToggle: (k: string | null) => void; children: ReactNode;
}) {
  const isOpen = open === metricKey;
  return (
    <button
      type="button"
      onClick={() => onToggle(isOpen ? null : metricKey)}
      aria-expanded={isOpen}
      title="Ver como este número foi calculado"
      className={`block w-full h-full text-left rounded-xl transition focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 [&:hover>div]:border-tm-300 ${isOpen ? "ring-2 ring-tm-500" : ""}`}
    >
      {children}
    </button>
  );
}

const CALC_TYPE_LABEL: Record<string, string> = {
  realized: "Realizado (documentos do período)",
  estimated: "Estimado (inclui parcela calculada)",
  derived: "Derivado (razão entre dois números da tela)",
};

/** Memória de cálculo de um KPI: fórmula, parcelas, fonte e limitações. */
function MetricMemoryPanel({ memory, periodLabel, onClose }: {
  memory: VehicleMetricMemory; periodLabel: string; onClose: () => void;
}) {
  const anchor = memory.detail?.kind === "fuel" ? "abastecimentos"
    : memory.detail?.kind === "maintenance" ? "manutencoes" : null;
  return (
    <section className="card overflow-hidden">
      <CardHead icon={<IconCalculator className="w-4 h-4" />} title={`Como foi calculado · ${memory.label}`}
        subtitle={`${periodLabel} · ${memory.formula}`} action={<CloseButton onClick={onClose} />} />
      <div className="p-5 grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 space-y-1.5">
          {memory.components.map((c, i) => {
            const total = c.label.startsWith("=");
            return total ? (
              <div key={i} className="mt-1 flex items-baseline justify-between gap-4 rounded-lg bg-tm-50 px-3 py-2 text-sm">
                <span className="font-semibold text-tm-800">{c.label}{c.hint && <span className="text-xs font-normal text-tm-700 ml-2">({c.hint})</span>}</span>
                <span className="font-semibold text-tm-800 tabular-nums whitespace-nowrap">{c.value}</span>
              </div>
            ) : (
              <div key={i} className="flex items-baseline justify-between gap-4 px-3 text-sm">
                <span className="text-gray-600">{c.label}{c.hint && <span className="text-xs text-gray-400 ml-2">({c.hint})</span>}</span>
                <span className="font-medium text-gray-900 tabular-nums whitespace-nowrap">{c.value}</span>
              </div>
            );
          })}
          {anchor && memory.detail && (
            <p className="px-3 pt-2 text-sm">
              <a href={`#${anchor}`} className="inline-flex items-center gap-1 font-medium text-tm-700 hover:underline">
                {memory.detail.label} <IconChevronDown className="w-3.5 h-3.5" />
              </a>
            </p>
          )}
        </div>
        <dl className="lg:col-span-2 rounded-lg bg-surface-muted px-4 py-3 space-y-2 text-xs text-gray-600">
          <div><dt className="font-semibold text-gray-900">Campo de data</dt><dd>{memory.dateFieldLabel}</dd></div>
          <div><dt className="font-semibold text-gray-900">Tipo</dt><dd>{CALC_TYPE_LABEL[memory.calculationType] ?? memory.calculationType}</dd></div>
          <div><dt className="font-semibold text-gray-900">Fonte</dt><dd>{memory.sourceTables.join(" · ")}</dd></div>
          {memory.limitations.length > 0 && (
            <div>
              <dt className="font-semibold text-gray-900">Limitações</dt>
              {memory.limitations.map((l, i) => <dd key={i} className="mt-0.5">{tidyText(l)}</dd>)}
            </div>
          )}
        </dl>
      </div>
    </section>
  );
}

const METODO_LABEL: Record<VehicleTollRouteGroup["metodo"], { title: string; hint: string }> = {
  viagem: { title: "por viagem", hint: "a passagem caiu na janela saída → chegada da viagem, e o frete é o de maior data até o instante da passagem" },
  cte_data: { title: "por data do CT-e", hint: "sem viagem na base: CT-e da placa emitido até 7 dias antes da passagem (aproximação, não prova de rota)" },
  sem_vinculo: { title: "sem vínculo", hint: "nenhuma viagem nem CT-e da placa cobre o instante da passagem" },
};

function rotuloGrupo(g: VehicleTollRouteGroup): string {
  const cte = g.cteNumber != null ? `CT-e ${g.cteSeries ? `${g.cteSeries}/` : ""}${g.cteNumber}` : null;
  const rota = g.originCity || g.destinationCity ? `${g.originCity ?? "?"} → ${g.destinationCity ?? "?"}` : null;
  if (g.metodo === "sem_vinculo") return "Sem viagem ou CT-e correspondente";
  return [cte, rota, g.customer].filter(Boolean).join(" · ");
}

/**
 * Memória do pedágio: as passagens que compõem o valor do custo direto,
 * agrupadas por rota. O método de atribuição vai junto do grupo, porque um número
 * atribuído "por data do CT-e" não tem a confiança de um "por viagem".
 */
function TollMemoryPanel({ mem, loading, error, onRetry, onClose }: { mem: VehicleTollsMemory | null; loading: boolean; error: boolean; onRetry: () => void; onClose: () => void }) {
  const [aberto, setAberto] = useState<string | null>(null);
  return (
    <section className="card overflow-hidden">
      <CardHead icon={<IconCalculator className="w-4 h-4" />} title="Como foi calculado · Pedágio"
        subtitle={error ? "Não foi possível carregar"
          : loading || !mem ? "Carregando…"
          : mem.source === "sem_parar"
            ? `${mem.count} passagem(ns) · ${mem.formattedTotal} pagos na praça (extrato Sem Parar ${mem.invoiceNumbers.join(", ")})`
            : mem.source === "sem_extrato"
              ? "Sem extrato Sem Parar importado para o período, valor desconhecido"
              : "Período coberto pelo extrato e nenhuma passagem desta placa"}
        action={<CloseButton onClick={onClose} />} />

      {error ? (
        <div className="p-4"><QueryErrorBanner what="as passagens de pedágio" onRetry={onRetry} /></div>
      ) : loading || !mem ? (
        <p className="px-5 py-4 text-sm text-gray-400">Carregando passagens…</p>
      ) : mem.groups.length === 0 ? (
        <p className="px-5 py-6 text-sm text-gray-400 text-center">
          {mem.source === "sem_extrato"
            ? "Importe o extrato Sem Parar do período em Frota › Pedágio para ver as passagens."
            : "Nenhuma passagem no período."}
        </p>
      ) : (
        <ul className="divide-y divide-surface-border">
          {mem.groups.map((g) => {
            const isOpen = aberto === g.key;
            const diff = g.freightTolls != null && g.freightTolls > 0 ? g.paidTolls - g.freightTolls : null;
            return (
              <li key={g.key}>
                <button
                  type="button"
                  onClick={() => setAberto(isOpen ? null : g.key)}
                  aria-expanded={isOpen}
                  className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-surface-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tm-500"
                >
                  {isOpen ? <IconChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <IconChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{rotuloGrupo(g)}</p>
                    <p className="text-xs text-gray-500">
                      <span title={METODO_LABEL[g.metodo].hint}>{METODO_LABEL[g.metodo].title}</span>
                      {g.from && g.to && ` · ${fmtDateShort(g.from)} → ${fmtDateShort(g.to)}`}
                      {` · ${g.count} passagem(ns)`}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900 tabular-nums">{g.formattedPaid}</p>
                    {g.freightTolls != null && (
                      <p className={`text-xs tabular-nums ${diff != null && Math.abs(diff) > 0.005 ? (diff > 0 ? "text-warning-700" : "text-tm-700") : "text-gray-400"}`}>
                        cobrado no frete {fmtBRL(g.freightTolls)}
                        {diff != null && ` · ${diff > 0 ? "+" : ""}${fmtBRL(diff)}`}
                      </p>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="overflow-x-auto bg-surface-muted/60">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-400">
                          <th className="pl-12 pr-3 py-1.5 text-left font-medium">Data e hora</th>
                          <th className="px-3 py-1.5 text-left font-medium">Concessionária · praça</th>
                          <th className="px-3 py-1.5 text-center font-medium">Cat.</th>
                          <th className="px-5 py-1.5 text-right font-medium">Valor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-border">
                        {g.passages.map((t) => (
                          <tr key={t.id} className="text-gray-700">
                            <td className="pl-12 pr-3 py-1.5 whitespace-nowrap">{fmtDateTime(t.occurredAt)}</td>
                            <td className="px-3 py-1.5">{t.description ?? "—"}</td>
                            <td className="px-3 py-1.5 text-center text-gray-500">{t.category ?? "—"}</td>
                            <td className={`px-5 py-1.5 text-right font-medium tabular-nums ${t.amount < 0 ? "text-tm-700" : ""}`}>{t.formattedAmount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {mem && mem.groups.length > 0 && (
        <p className="px-5 py-3 text-[11px] text-gray-500 border-t border-surface-border leading-snug">
          Base para atribuir: {mem.basis.trips} viagem(ns), {mem.basis.freights} frete(s) de viagem e {mem.basis.ctes} CT-e da placa no período.
          Passagens em hora local do extrato. "Por viagem" é o vínculo confiável e "por data do CT-e" é aproximação.
        </p>
      )}
    </section>
  );
}

type RaioXAba = "visao" | "pneus";

function RaioXTabs({ aba, onChange }: { aba: RaioXAba; onChange: (a: RaioXAba) => void }) {
  return (
    <div className="-mt-2">
      <TabBar<RaioXAba> tabs={[{ key: "visao", label: "Visão do veículo" }, { key: "pneus", label: "Pneus" }]} active={aba} onChange={onChange} />
    </div>
  );
}

/** Variação contra o período anterior só quando a placa existia nele. */
function prevDelta(found: boolean, cur: number | null | undefined, prev: number | null | undefined, direction: "higher_is_better" | "higher_is_worse", minBase?: number): DeltaResult | null {
  if (!found) return null;
  return computeDelta(cur ?? null, prev ?? null, { direction, minBase });
}

const CMP_LABEL = "vs. período anterior";

export function VehicleDrilldownPage() {
  const { plate = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const aba: RaioXAba = params.get("aba") === "pneus" ? "pneus" : "visao";
  const setAba = (a: RaioXAba) => {
    const p = new URLSearchParams(params);
    if (a === "visao") p.delete("aba"); else p.set("aba", a);
    setParams(p, { replace: true });
  };
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "vehicle-drilldown", plate, from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportVehicleDrilldownDto>(
        `/dashboards/transport/vehicle/${encodeURIComponent(plate)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
    enabled: plate.length >= 5,
  });

  // Memória de cálculo: qualquer KPI abre fórmula + componentes + limitações. O card de
  // faturamento também traz os DOCUMENTOS (lista de CT-e da placa no período).
  const [openMetric, setOpenMetric] = useState<string | null>(null);
  const showCtes = openMetric === "revenue";
  const showTolls = openMetric === "tolls";
  // O card de KM abre a linha do tempo: os trechos que explicam (ou não) o número.
  const showKmTimeline = openMetric === "km";
  const { data: cteList, isLoading: cteLoading, isError: cteListError, refetch: cteListRefetch } = useQuery({
    queryKey: ["transport", "vehicle-ctes", plate, from, to],
    queryFn: () =>
      api.get<VehicleCtesResponse>(
        `/dashboards/transport/vehicle/${encodeURIComponent(plate)}/ctes?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    staleTime: 60_000,
    enabled: showCtes && plate.length >= 5,
  });

  const { data: tollMem, isLoading: tollLoading, isError: tollError, refetch: tollRefetch } = useQuery({
    queryKey: ["transport", "vehicle-tolls", plate, from, to],
    queryFn: () =>
      api.get<VehicleTollsMemory>(
        `/dashboards/transport/vehicle/${encodeURIComponent(plate)}/tolls?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: showTolls && plate.length >= 5,
  });


  // Aba "Pneus": não depende do período nem de haver CT-e ou abastecimento. O mapa
  // físico do veículo existe mesmo num mês sem movimento.
  if (aba === "pneus") {
    return (
      <div className="space-y-6">
        <PageHeader title={`Frota · Raio-X ${plate.toUpperCase()}`} subtitle="Pneus: configuração, mapa dos eixos e ações por posição" />
        <RaioXTabs aba={aba} onChange={setAba} />
        <VehicleTiresTab plate={plate} />
      </div>
    );
  }

  if (isLoading) return <PageSkeleton />;

  if (!data || !data.found) {
    return (
      <div className="space-y-6">
        <PageHeader title={`Frota · Raio-X ${plate.toUpperCase()}`} subtitle="Visão única do veículo no período" />
        <RaioXTabs aba={aba} onChange={setAba} />
        {dataError ? (
          <QueryErrorBanner what="o Raio-X desta placa" onRetry={() => { dataRefetch(); }} />
        ) : (
        <EmptyState
          icon={<IconTruck className="w-7 h-7" />}
          title="Sem dados para esta placa no período"
          description="Nenhum CT-e, abastecimento, manutenção ou pneu encontrado. Ajuste o período no filtro global ou verifique a placa."
        />
        )}
        <p className="text-sm text-gray-400"><Link to="/frota/margem" className="text-tm-700 hover:underline">← Voltar para Margem por Placa</Link></p>
      </div>
    );
  }

  const d = data;
  const r = d.result;
  const prev = r.previous;
  const openMemory = d.metricMemories.find((m) => m.key === openMetric) ?? null;
  const points = vehicleAttention(d);
  const heroStatus = d.rig.consolidatedInto ? "ok" : vehicleHeroStatus(d, points);

  const directTotal = d.costs.direct.total;
  const directParts = [
    { key: "combustivel", label: "Combustível", value: d.costs.direct.fuel },
    { key: "manutencao", label: "Manutenção", value: d.costs.direct.maintenance },
    { key: "motorista", label: "Motorista", value: d.costs.direct.driverPay + d.costs.direct.commission },
    { key: "pedagio", label: "Pedágio", value: d.costs.direct.tolls },
    { key: "despesas", label: "Despesas de viagem", value: d.costs.direct.tripExpenses },
    { key: "seguro", label: d.costs.direct.seguroIsReal ? "Seguro" : "Seguro (estimado)", value: d.costs.direct.seguro },
  ];
  const financialTop = d.costs.financial.byCategory.slice(0, 8);

  const kml = d.operation.kmPerLiter;
  const consumptionOk = d.operation.consumptionReliable;
  const hasRevenue = d.production.revenue > 0;

  // Manchete: o número que importa da placa no período, numa frase.
  const headline = d.rig.consolidatedInto ? (
    <>{d.plate} é uma carreta e os números dela estão no cavalo <HeroNumber>{d.rig.consolidatedInto}</HeroNumber>.</>
  ) : hasRevenue ? (
    <>{d.plate} deixou <HeroNumber>{fmtBRL(r.margin)}</HeroNumber> de margem operacional sobre {fmtBRL(d.production.revenue)} faturados{r.marginPct != null ? ` (${fmtPct(r.marginPct * 100)})` : ""}.</>
  ) : (
    <>{d.plate} não faturou no período e teve <HeroNumber>{fmtBRL(directTotal)}</HeroNumber> de custo direto.</>
  );
  const detailParts = [
    `${d.production.cteCount} CT-e`,
    d.production.km > 0 ? `${fmtNumber(Math.round(d.production.km))} km rodados` : null,
    d.driver ? `motorista ${d.driver}` : null,
    d.rig.trailers.length > 0 ? `conjunto com ${d.rig.trailers.join(" e ")}` : null,
  ].filter(Boolean);
  const detail = d.rig.consolidatedInto
    ? "Ela não emite CT-e nem abastece. O custo dela entra na margem do conjunto."
    : `${detailParts.join(", ")}. Período: ${d.period.label}.`;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Frota · Raio-X ${d.plate}`}
        subtitle="Visão única do veículo no período: resultado, custos, operação e conferências"
        actions={<StatusPill status={r.status} />}
      />
      <RaioXTabs aba={aba} onChange={setAba} />
      {dataError && <QueryErrorBanner what="o Raio-X desta placa" onRetry={() => { dataRefetch(); }} />}
      <TollCoverageNotice coverage={d.tollCoverage} />

      {/* Faixa-manchete, no mesmo desenho da Visão Executiva */}
      <SystemHero
        status={heroStatus}
        statusText={d.rig.consolidatedInto ? undefined : points.length > 0 ? `${points.length} ponto${points.length > 1 ? "s" : ""} de atenção` : "nada pede ação"}
        headline={headline}
        detail={detail}
      />

      {/* Contexto do conjunto cavalo e carreta */}
      {(d.rig.consolidatedInto || (d.rig.trailers.length > 0 && d.rig.trailerCost > 0) || d.telemetryOdometer) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {d.rig.consolidatedInto && (
            <Notice>
              <strong>{d.plate}</strong> é uma <strong>carreta</strong>: não emite CT-e nem abastece. O custo dela
              está consolidado no cavalo que a puxa.{" "}
              <Link to={`/frota/placa/${encodeURIComponent(d.rig.consolidatedInto)}`} className="font-medium text-tm-700 hover:underline">
                Ver o conjunto em {d.rig.consolidatedInto}
              </Link>
            </Notice>
          )}
          {d.rig.trailers.length > 0 && d.rig.trailerCost > 0 && (
            <Notice>
              Deste custo direto, <strong>{fmtBRL(d.rig.trailerCost)}</strong> vêm da(s) carreta(s){" "}
              {d.rig.trailers.map((t, i) => (
                <span key={t}>
                  {i > 0 && " · "}
                  <Link to={`/frota/placa/${encodeURIComponent(t)}`} className="font-medium text-tm-700 hover:underline">{t}</Link>
                </span>
              ))}{" "}
              (vínculo do cadastro Sinersoft ou reboque da viagem).
            </Notice>
          )}
          {/* Odômetro da telemetria, CREDITANDO a fonte. Não substitui o KM do
              abastecimento: os dois números convivem à vista. */}
          {d.telemetryOdometer && (
            <Notice>
              <strong>Odômetro {fmtNumber(Math.round(d.telemetryOdometer.km))} km</strong>, leitura do rastreador {d.telemetryOdometer.source} em{" "}
              {new Date(d.telemetryOdometer.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}.
              O KM Rodado do período continua vindo do abastecimento, digitado no posto. Os dois aparecem juntos de propósito.
            </Notice>
          )}
        </div>
      )}

      {!d.rig.consolidatedInto && (
        <AttentionPanel points={points}
          empty={{ title: "Nada pede ação nesta placa", text: "Margem, custo por km, consumo, pneus e as conferências de KM e pedágio estão dentro dos limites acompanhados." }} />
      )}

      {/* KPIs: todos abrem a memória de cálculo logo abaixo da grade */}
      <section aria-labelledby="indicadores-placa" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="indicadores-placa" className="font-display font-semibold text-lg text-gray-900">Indicadores do veículo</h2>
          <p className="text-xs text-gray-600">Clique em um indicador para ver como foi calculado</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCardButton metricKey="revenue" open={openMetric} onToggle={setOpenMetric}>
            <KpiTile label="Faturamento" value={fmtBRL(d.production.revenue)}
              delta={prevDelta(prev.found, d.production.revenue, prev.revenue, "higher_is_better")} deltaLabel={CMP_LABEL}
              footnote={`${d.production.cteCount} CT-e no período, com a lista dos documentos`} />
          </MetricCardButton>
          <MetricCardButton metricKey="km" open={openMetric} onToggle={setOpenMetric}>
            <KpiTile label="KM rodado" value={d.production.km > 0 ? fmtNumber(Math.round(d.production.km)) : "—"}
              footnote={d.production.km > 0 ? "Apurado nos abastecimentos, com a linha do tempo das viagens" : "Sem km apurado no período"} />
          </MetricCardButton>
          <MetricCardButton metricKey="margin" open={openMetric} onToggle={setOpenMetric}>
            <KpiTile label="Margem operacional" value={fmtBRL(r.margin)}
              delta={prevDelta(prev.found, r.margin, prev.margin, "higher_is_better")} deltaLabel={CMP_LABEL}
              status={marginTone(r.marginPct)}
              visual={hasRevenue ? (
                <SplitMeter parts={[
                  { label: "Custo", value: directTotal, display: fmtPct((directTotal / d.production.revenue) * 100), tone: "danger" },
                  { label: "Margem", value: Math.max(0, r.margin), display: r.marginPct != null ? fmtPct(r.marginPct * 100) : "—", tone: "brand" },
                ]} />
              ) : undefined}
              footnote="Parcial, só custos diretos" />
          </MetricCardButton>
          <MetricCardButton metricKey="cost_per_km" open={openMetric} onToggle={setOpenMetric}>
            <KpiTile label="Custo por km" value={r.costPerKm != null ? brlKm(r.costPerKm) : "—"}
              delta={prevDelta(prev.found, r.costPerKm, prev.costPerKm, "higher_is_worse", 1)} deltaLabel={CMP_LABEL}
              status={costPerKmTone(r.costPerKm, r.fleet.costPerKm)}
              footnote={vsFleet(r.costPerKm, r.fleet.costPerKm, brlKm) ?? "Sem base de comparação com a frota"} />
          </MetricCardButton>
          <MetricCardButton metricKey="km_per_liter" open={openMetric} onToggle={setOpenMetric}>
            <KpiTile label="Consumo (km/l diesel)" value={kml != null ? fmt2(kml) : "—"}
              delta={consumptionOk ? prevDelta(prev.found, kml, prev.kmPerLiter, "higher_is_better", 1) : null} deltaLabel={CMP_LABEL}
              status={consumptionTone(kml, r.fleet.kmPerLiter, consumptionOk)}
              footnote={
                kml == null ? "Sem abastecimento diesel no período"
                : !consumptionOk ? `Base insuficiente (${d.operation.dieselRefuels} abastecimentos), fora dos alertas`
                : vsFleet(kml, r.fleet.kmPerLiter, (v) => `${fmt2(v)} km/l`) ?? undefined
              } />
          </MetricCardButton>
          <MetricCardButton metricKey="revenue_per_km" open={openMetric} onToggle={setOpenMetric}>
            <KpiTile label="Receita por km" value={d.production.revenuePerKm != null ? brlKm(d.production.revenuePerKm) : "—"}
              footnote={vsFleet(d.production.revenuePerKm, r.fleet.revenuePerKm, brlKm) ?? "Sem base de comparação com a frota"} />
          </MetricCardButton>
        </div>
      </section>

      {/* Memória de cálculo do KPI aberto (fórmula, parcelas, fonte e limitações) */}
      {openMemory && (
        <MetricMemoryPanel memory={openMemory} periodLabel={d.period.label} onClose={() => setOpenMetric(null)} />
      )}

      {/* Drill do KM: a linha do tempo operacional que explica o número do card */}
      {showKmTimeline && (
        <KmTimelinePanel plate={plate} from={from} to={to} onClose={() => setOpenMetric(null)} />
      )}

      {/* Drill do pedágio: as passagens que compõem o valor, por rota e CT-e */}
      {showTolls && (
        <TollMemoryPanel mem={tollMem ?? null} loading={tollLoading} error={tollError} onRetry={() => { tollRefetch(); }} onClose={() => setOpenMetric(null)} />
      )}

      {/* Drill do faturamento: lista dos CT-e do veículo no período */}
      {showCtes && (
        <section className="card overflow-hidden">
          <CardHead icon={<IconDocument className="w-4 h-4" />} title="CT-e do veículo no período"
            subtitle={`${cteList ? `${cteList.count} CT-e · ${cteList.formattedTotal}` : cteListError ? "Não foi possível carregar" : "Carregando…"} · autorizados (status 2), por data de emissão`}
            action={<CloseButton onClick={() => setOpenMetric(null)} />} />
          {cteListError && !cteList ? (
            <div className="p-4"><QueryErrorBanner what="a lista de CT-e" onRetry={() => { cteListRefetch(); }} /></div>
          ) : cteLoading ? (
            <p className="px-5 py-4 text-sm text-gray-400">Carregando CT-e…</p>
          ) : !cteList || cteList.ctes.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">Nenhum CT-e autorizado no período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-gray-400 text-xs">
                    <th className="px-5 py-2 text-left font-medium">CT-e</th>
                    <th className="px-3 py-2 text-left font-medium">Emissão</th>
                    <th className="px-3 py-2 text-left font-medium">Cliente</th>
                    <th className="px-5 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {cteList.ctes.map((c, i) => (
                    <tr key={`${c.serie}-${c.numero}-${i}`} className="text-gray-700 hover:bg-surface-muted/50">
                      <td className="px-5 py-2.5 font-mono text-gray-500">{c.serie ? `${c.serie}/` : ""}{c.numero}</td>
                      <td className="px-3 py-2.5">{fmtDateShort(c.issueDate)}</td>
                      <td className="px-3 py-2.5 max-w-[280px] truncate" title={c.customer ?? ""}>{c.customer ?? "—"}</td>
                      <td className="px-5 py-2.5 text-right font-semibold text-gray-900 tabular-nums">{c.formattedValue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Leitura do período: contexto em frases, sem desvio que peça ação */}
      {d.reading.length > 0 && (
        <section className="card overflow-hidden">
          <CardHead icon={<IconLightbulb className="w-4 h-4 text-tm-600" />} title="Leitura do período"
            subtitle="O que os números desta placa dizem, em frases" />
          <ul className="px-5 py-4 space-y-2.5">
            {d.reading.map((line, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-gray-700 leading-snug">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-tm-400 flex-shrink-0" aria-hidden="true" />
                {tidyText(line)}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Custos: direto × financeiro + comparação com período anterior */}
      <section aria-labelledby="custos-placa" className="space-y-3">
        <h2 id="custos-placa" className="font-display font-semibold text-lg text-gray-900">Para onde foi o custo</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card overflow-hidden flex flex-col">
            <CardHead icon={<IconDollar className="w-4 h-4" />} title="Custo direto (margem)" subtitle="Composição usada na margem operacional" />
            <div className="p-5 space-y-3 flex-1">
              {directParts.map((c) => {
                const share = directTotal > 0 ? (c.value / directTotal) * 100 : 0;
                const isToll = c.key === "pedagio";
                const semExtrato = isToll && d.costs.direct.tollsSource === "sem_extrato";
                const line = (
                  <>
                    <div className="flex items-baseline gap-2 text-sm">
                      <span className={isToll ? "text-tm-700 font-medium underline decoration-dotted underline-offset-2" : c.value === 0 ? "text-gray-400" : "text-gray-700"}>{c.label}</span>
                      <span className="ml-auto font-semibold text-gray-900 whitespace-nowrap tabular-nums">
                        {semExtrato ? <span className="text-xs font-normal text-gray-400" title={SEM_EXTRATO_TITLE}>sem extrato</span> : fmtBRL(c.value)}
                      </span>
                      <span className="w-12 text-right text-xs text-gray-400">{c.value > 0 ? pctFmt(share) : ""}</span>
                    </div>
                    {c.value > 0 && <div className="mt-1.5"><ShareBar pct={share} color={DIRECT_COLOR[c.key] ?? "#9CA3AF"} /></div>}
                  </>
                );
                return isToll ? (
                  <button key={c.key} type="button"
                    onClick={() => setOpenMetric(showTolls ? null : "tolls")}
                    aria-expanded={showTolls}
                    title="Ver as passagens que compõem este valor, por rota e CT-e"
                    className={`block w-[calc(100%+1rem)] text-left rounded-lg px-2 py-1.5 -mx-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${showTolls ? "bg-tm-50 ring-1 ring-tm-300" : "hover:bg-surface-muted"}`}>
                    {line}
                  </button>
                ) : <div key={c.key}>{line}</div>;
              })}
              {d.costs.direct.tires > 0 && (
                <div className="flex items-baseline gap-2 text-sm text-gray-400">
                  <span>Pneus (fora da margem)</span>
                  <span className="ml-auto whitespace-nowrap tabular-nums">{fmtBRL(d.costs.direct.tires)}</span>
                  <span className="w-12" />
                </div>
              )}
            </div>
            <div className="mx-5 mb-5 rounded-lg bg-surface-muted px-3 py-2.5 flex items-baseline gap-2">
              <span className="text-sm font-semibold text-gray-900">Total</span>
              <span className="ml-auto text-base font-bold text-gray-900 tabular-nums">{fmtBRL(directTotal)}</span>
            </div>
          </div>

          <div className="card overflow-hidden flex flex-col">
            <CardHead icon={<IconDocument className="w-4 h-4" />} title="Custo financeiro alocado" subtitle="Rateio de contas a pagar por centro de custo (pagos e abertos)" />
            {!d.costs.financial.available ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center flex-1">Sem rateio financeiro para esta placa no período.</p>
            ) : (
              <>
                <div className="p-5 space-y-3 flex-1">
                  {financialTop.map((c, i) => {
                    const share = d.costs.financial.total > 0 ? (c.total / d.costs.financial.total) * 100 : 0;
                    return (
                      <div key={c.category}>
                        <div className="flex items-baseline gap-2 text-sm">
                          <span className="text-gray-700 truncate">{catLabel(c.category)}</span>
                          <span className="ml-auto font-semibold text-gray-900 whitespace-nowrap tabular-nums">{fmtBRL(c.total)}</span>
                          <span className="w-12 text-right text-xs text-gray-400">{pctFmt(share)}</span>
                        </div>
                        <div className="mt-1.5"><ShareBar pct={share} color={c.category === "outros" ? "#CBD5E1" : CATEGORY_COLORS[i % CATEGORY_COLORS.length]!} /></div>
                      </div>
                    );
                  })}
                </div>
                <div className="mx-5 mb-5 rounded-lg bg-surface-muted px-3 py-2.5 flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-gray-900">Total alocado</span>
                  <span className="ml-auto text-base font-bold text-gray-900 tabular-nums">{fmtBRL(d.costs.financial.total)}</span>
                </div>
              </>
            )}
          </div>

          <div className="card overflow-hidden flex flex-col">
            <CardHead icon={<IconLineChart className="w-4 h-4" />} title="Este período × anterior" subtitle={tidyText(d.comparisonLabel)} />
            {!prev.found ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center flex-1">Sem dados desta placa no período anterior.</p>
            ) : (
              <ul className="divide-y divide-surface-border flex-1">
                {[
                  { label: "Faturamento", cur: fmtBRL(d.production.revenue), old: fmtBRL(prev.revenue), delta: computeDelta(d.production.revenue, prev.revenue, { direction: "higher_is_better" }) },
                  { label: "Margem", cur: fmtBRL(r.margin), old: `${fmtBRL(prev.margin)}${prev.marginPct != null ? ` (${fmtPct(prev.marginPct * 100)})` : ""}`, delta: computeDelta(r.margin, prev.margin, { direction: "higher_is_better" }), negative: prev.margin < 0 },
                  { label: "Custo por km", cur: r.costPerKm != null ? brlKm(r.costPerKm) : "—", old: prev.costPerKm != null ? brlKm(prev.costPerKm) : "—", delta: computeDelta(r.costPerKm, prev.costPerKm, { direction: "higher_is_worse", minBase: 1 }) },
                  { label: "Consumo (km/l)", cur: kml != null ? fmt2(kml) : "—", old: prev.kmPerLiter != null ? fmt2(prev.kmPerLiter) : "—", delta: computeDelta(kml, prev.kmPerLiter, { direction: "higher_is_better", minBase: 1 }) },
                ].map((row) => (
                  <li key={row.label} className="px-5 py-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-600">{row.label}</span>
                      <span className="ml-auto text-sm font-semibold text-gray-900 tabular-nums whitespace-nowrap">{row.cur}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-end gap-2 text-xs">
                      <span className={`tabular-nums whitespace-nowrap ${row.negative ? "text-danger-600" : "text-gray-500"}`}>antes {row.old}</span>
                      {(row.delta.pct != null || (row.delta.label && row.delta.label !== "—")) && <DeltaChip delta={row.delta} />}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Conferências: duas fontes lado a lado, nenhuma substitui a outra. A
          diferença é o achado, e o aviso diz quando ela NÃO deve ser lida como erro. */}
      {(d.kmAudit || d.tollAudit) && (
        <section aria-labelledby="conferencias-placa" className="space-y-3">
          <h2 id="conferencias-placa" className="font-display font-semibold text-lg text-gray-900">Conferências</h2>
          <div className={`grid grid-cols-1 ${d.kmAudit && d.tollAudit ? "xl:grid-cols-2" : ""} gap-4`}>
            {d.kmAudit && (
              <div className="card overflow-hidden flex flex-col">
                <CardHead icon={<IconTruck className="w-4 h-4" />} title={`KM: abastecimento × telemetria (${d.kmAudit.source})`}
                  subtitle="O KM do abastecimento vem do hodômetro digitado no posto. O da telemetria é a diferença de odômetro lida pelo rastreador." />
                <div className="p-5 space-y-3 flex-1">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <AuditStat label="KM no abastecimento" value={d.kmAudit.fuelKm != null ? fmtNumber(Math.round(d.kmAudit.fuelKm)) : "—"} />
                    <AuditStat label="KM na telemetria" value={d.kmAudit.telemetryKm != null ? fmtNumber(Math.round(d.kmAudit.telemetryKm)) : "—"} />
                    <AuditStat label="Diferença"
                      tone={d.kmAudit.diffKm == null ? "muted" : Math.abs(d.kmAudit.diffPct ?? 0) > 0.1 ? "warning" : null}
                      value={d.kmAudit.diffKm != null ? `${d.kmAudit.diffKm > 0 ? "+" : ""}${fmtNumber(Math.round(d.kmAudit.diffKm))}` : "—"}
                      note={d.kmAudit.diffPct != null ? `${(d.kmAudit.diffPct * 100).toFixed(1).replace(".", ",")}%` : undefined} />
                    <AuditStat label="km/l: abast. → telemetria"
                      value={`${d.kmAudit.kmPerLiterFuel != null ? fmt2(d.kmAudit.kmPerLiterFuel) : "—"} → ${d.kmAudit.kmPerLiterTelemetry != null ? fmt2(d.kmAudit.kmPerLiterTelemetry) : "—"}`} />
                  </div>
                  {/* Cobertura: sem isto, um km menor por falta de leitura pareceria menos rodagem. */}
                  {d.kmAudit.coverage && (
                    <div>
                      <div className="flex items-baseline justify-between text-xs text-gray-600 mb-1.5">
                        <span>Cobertura da telemetria: {d.kmAudit.coverage.readings} leitura(s) de {new Date(d.kmAudit.coverage.firstAt).toLocaleDateString("pt-BR")} a {new Date(d.kmAudit.coverage.lastAt).toLocaleDateString("pt-BR")}</span>
                        <span className="font-semibold text-gray-900 tabular-nums">{Math.round(d.kmAudit.coverage.coversPct * 100)}%</span>
                      </div>
                      <ShareBar pct={d.kmAudit.coverage.coversPct * 100} color={d.kmAudit.coverage.coversPct >= 0.8 ? "rgb(var(--tm-400))" : "#F59E0B"} />
                    </div>
                  )}
                  {d.kmAudit.status === "cobertura_parcial" && (
                    <Notice tone="warning">
                      A telemetria cobriu só parte do período, então o KM dela está menor por falta de leitura, não por menos
                      rodagem. A diferença acima <strong>não</strong> deve ser lida como divergência.
                    </Notice>
                  )}
                  {d.kmAudit.status === "divergencia_alta" && (
                    <Notice tone="warning">
                      Divergência acima de 10% com boa cobertura da telemetria. Vale conferir os hodômetros digitados nos
                      abastecimentos do período.
                    </Notice>
                  )}
                  {d.kmAudit.status === "sem_telemetria" && (
                    <Notice>Sem leitura de telemetria no período. Só o lado do abastecimento está disponível.</Notice>
                  )}
                </div>
              </div>
            )}

            {d.tollAudit && (
              <div className="card overflow-hidden flex flex-col">
                <CardHead icon={<IconDollar className="w-4 h-4" />} title="Pedágio: pago × cobrado no frete"
                  subtitle={`O pago vem do extrato ${d.tollAudit.source} importado. O cobrado é o pedágio do frete no ERP. Um é despesa efetiva, o outro é o que foi contratado.`} />
                <div className="p-5 space-y-3 flex-1">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <AuditStat label={`Pago (${d.tollAudit.source})`} value={fmtBRL(d.tollAudit.paidTolls)} note={`${d.tollAudit.tollCount} passagem(ns)`} />
                    <AuditStat label="Cobrado no frete"
                      tone={d.tollAudit.freightTolls ? null : "muted"}
                      value={d.tollAudit.freightTolls ? fmtBRL(d.tollAudit.freightTolls) : "—"}
                      note={!d.tollAudit.freightTolls ? "Sem frete de viagem com pedágio no período" : undefined} />
                    <AuditStat label="Diferença"
                      tone={d.tollAudit.diffValue == null ? "muted" : Math.abs(d.tollAudit.diffPct ?? 0) > 0.1 ? "warning" : null}
                      value={d.tollAudit.diffValue != null ? `${d.tollAudit.diffValue > 0 ? "+" : ""}${fmtBRL(d.tollAudit.diffValue)}` : "—"}
                      note={d.tollAudit.diffPct != null ? `${(d.tollAudit.diffPct * 100).toFixed(1).replace(".", ",")}%` : d.tollAudit.diffValue == null ? "Sem base de comparação" : undefined} />
                    <AuditStat label="Fatura(s)" value={<span className="text-sm">{d.tollAudit.invoiceNumbers.join(", ") || "—"}</span>} />
                  </div>
                  {d.tollAudit.paidInPeriodCount > 0 && (
                    <p className="text-xs text-gray-600 leading-snug">
                      No custo direto desta placa entrou o <strong>pago no período da tela</strong>: {fmtBRL(d.tollAudit.paidInPeriod)} em {d.tollAudit.paidInPeriodCount} passagem(ns).
                    </p>
                  )}
                  {/* O ciclo da fatura não é mês-calendário. Sem dizer o período real, a
                      diferença acima pareceria divergência quando é só recorte. */}
                  {d.tollAudit.invoicePeriod && (
                    <p className="text-xs text-gray-600">
                      Período coberto pela fatura:{" "}
                      {new Date(d.tollAudit.invoicePeriod.from).toLocaleDateString("pt-BR", { timeZone: "UTC" })} a{" "}
                      {new Date(d.tollAudit.invoicePeriod.to).toLocaleDateString("pt-BR", { timeZone: "UTC" })}.
                    </p>
                  )}
                  {d.tollAudit.periodMismatch && (
                    <Notice tone="warning">
                      O ciclo da fatura não coincide com o período da tela. A diferença acima inclui esse descasamento e não
                      deve ser lida como divergência de valor.
                    </Notice>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Operação: pneus, abastecimentos, checklists e manutenções */}
      <section aria-labelledby="operacao-placa" className="space-y-3">
        <h2 id="operacao-placa" className="font-display font-semibold text-lg text-gray-900">Operação no período</h2>

        <div className="card overflow-hidden">
          <CardHead icon={<IconTire className="w-4 h-4" />} title={`Pneus instalados (${d.operation.tires.length})`}
            subtitle={d.operation.tiresSource ? `Fonte: ${d.operation.tiresSource}` : "Sem pneus vinculados a esta placa na fonte ativa"}
            action={<button type="button" onClick={() => setAba("pneus")} className="text-xs font-medium text-tm-700 hover:underline flex-shrink-0">Abrir a aba Pneus</button>} />
          {d.operation.tires.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-surface-border">
                    <th className="px-5 py-2 text-left font-medium">Nº de fogo</th>
                    <th className="px-3 py-2 text-left font-medium">Marca</th>
                    <th className="px-3 py-2 text-left font-medium">Posição</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Vida</th>
                    <th className="px-3 py-2 text-right font-medium">Sulco (mm)</th>
                    <th className="px-3 py-2 text-right font-medium">Limite (mm)</th>
                    <th className="px-3 py-2 text-left font-medium w-40">Vida usada</th>
                    <th className="px-5 py-2 text-right font-medium">Recap.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {d.operation.tires.map((t, i) => {
                    const used = t.lifeUsedPct;
                    const critical = used != null && used >= 0.9;
                    return (
                      <tr key={i} className="text-gray-600 hover:bg-surface-muted/50">
                        <td className="px-5 py-2.5 font-medium text-gray-900">{t.fireNumber ?? "—"}</td>
                        <td className="px-3 py-2.5">{t.brand ?? "—"}</td>
                        <td className="px-3 py-2.5">{t.position ?? "—"}</td>
                        <td className="px-3 py-2.5">{t.status ?? "—"}</td>
                        <td className="px-3 py-2.5">{t.lifeStage ?? "—"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{t.currentMm != null ? fmt2(t.currentMm) : "—"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">{t.mmSafety != null ? fmt2(t.mmSafety) : "—"}</td>
                        <td className="px-3 py-2.5">
                          {used != null ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1"><ShareBar pct={used * 100} color={critical ? "#EF4444" : "rgb(var(--tm-400))"} /></div>
                              <span className={`w-12 text-right text-xs font-semibold tabular-nums ${critical ? "text-danger-600" : "text-gray-700"}`}>{fmtPct(used * 100)}</span>
                            </div>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-2.5 text-right tabular-nums">{t.recapCount ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card overflow-hidden scroll-mt-4" id="abastecimentos">
            <CardHead icon={<IconFuel className="w-4 h-4" />} title={`Abastecimentos no período (${d.operation.fuelCount})`}
              subtitle={d.operation.fuelCount > d.operation.fuelHistory.length
                ? `Exibindo os ${d.operation.fuelHistory.length} mais recentes. Linha riscada não entrou no KM.`
                : "Linha riscada não entrou no KM (arla, gasolina ou hodômetro fora da faixa)"} />
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-xs text-gray-400 border-b border-surface-border">
                    <th className="px-5 py-2 text-left font-medium">Data</th>
                    <th className="px-3 py-2 text-left font-medium">Comb.</th>
                    <th className="px-3 py-2 text-right font-medium">Litros</th>
                    <th className="px-3 py-2 text-right font-medium">R$/L</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                    <th className="px-3 py-2 text-right font-medium">Hodômetro</th>
                    <th className="px-5 py-2 text-right font-medium">KM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {d.operation.fuelHistory.length === 0 ? (
                    <tr><td colSpan={7} className="px-5 py-6 text-center text-gray-400">Sem abastecimentos no período.</td></tr>
                  ) : d.operation.fuelHistory.map((f, i) => (
                    <tr key={i} className="text-gray-600">
                      <td className="px-5 py-2">{fmtDateShort(f.date)}</td>
                      <td className="px-3 py-2">{f.fuelType ?? "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmt2(f.liters)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{f.pricePerLiter != null ? fmt2(f.pricePerLiter) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{fmtBRL(f.total)}</td>
                      {/* Km Inicial → Km Atual, as mesmas colunas da Lista de
                          Abastecimentos do SinerSoft, para conferir linha a linha. */}
                      <td className="px-3 py-2 text-right text-xs text-gray-500 whitespace-nowrap tabular-nums">
                        {f.odometerStart != null && f.odometerEnd != null
                          ? `${f.odometerStart.toLocaleString("pt-BR")} → ${f.odometerEnd.toLocaleString("pt-BR")}`
                          : "—"}
                      </td>
                      {/* Linha que NÃO entrou no KM aparece esmaecida e riscada: o
                          número está na origem, mas não soma, e a coluna precisa
                          fechar com o card. */}
                      <td className={`px-5 py-2 text-right tabular-nums ${f.countedInKm ? "text-gray-600" : "text-gray-300 line-through"}`}
                        title={f.countedInKm ? undefined : "Fora do KM: arla, gasolina ou hodômetro fora da faixa possível"}>
                        {f.km != null ? Math.round(f.km).toLocaleString("pt-BR") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <PlateRunHistory plate={d.plate} />

          <div className="card overflow-hidden scroll-mt-4" id="manutencoes">
            <CardHead icon={<IconWrench className="w-4 h-4" />} title={`Manutenções no período (${d.operation.maintenanceCount})`}
              subtitle={`OS interna: mão de obra não valorada na origem (peças apenas)${d.operation.maintenanceCount > d.operation.maintenanceHistory.length
                ? `. Exibindo as ${d.operation.maintenanceHistory.length} mais recentes.` : ""}`}
              action={
                <Link to={`/frota/manutencao/checklists?plate=${encodeURIComponent(d.plate)}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-tm-700 hover:underline flex-shrink-0">
                  Checklists desta placa <IconChevronRight className="w-3 h-3" />
                </Link>
              } />
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-xs text-gray-400 border-b border-surface-border">
                    <th className="px-5 py-2 text-left font-medium">Data</th>
                    <th className="px-3 py-2 text-left font-medium">Origem</th>
                    <th className="px-3 py-2 text-right font-medium">Peças</th>
                    <th className="px-3 py-2 text-right font-medium">Mão de obra</th>
                    <th className="px-5 py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {d.operation.maintenanceHistory.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">Sem manutenções no período.</td></tr>
                  ) : d.operation.maintenanceHistory.map((m, i) => (
                    <tr key={i} className="text-gray-600">
                      <td className="px-5 py-2">{fmtDateShort(m.date)}</td>
                      <td className="px-3 py-2">{m.origin === "internal" ? "Oficina própria" : "Terceiros"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBRL(m.parts)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{m.origin === "internal" && m.labor === 0 ? "n/d" : fmtBRL(m.labor)}</td>
                      <td className="px-5 py-2 text-right tabular-nums font-semibold text-gray-900">{fmtBRL(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Qualidade dos dados + fontes */}
      <section className="card overflow-hidden">
        <CardHead icon={<IconDatabase className="w-4 h-4" />} title="De onde vêm estes números" />
        <div className="p-5 space-y-3">
          {d.dataQuality.length > 0 && (
            <Notice tone="warning">
              <p className="font-semibold mb-1">Avisos de qualidade dos dados</p>
              <ul className="space-y-0.5">
                {d.dataQuality.map((q, i) => <li key={i}>{tidyText(q)}</li>)}
              </ul>
            </Notice>
          )}
          <p className="text-xs text-gray-600 leading-relaxed">
            Margem <strong>parcial</strong>: custos diretos (combustível, manutenção, motorista, pedágio, despesas de viagem e seguro{d.costs.direct.seguroIsReal ? "" : " estimado"}).
            Não inclui pneus, IPVA, licenciamento, financiamento e custos administrativos. Motorista, pedágio e seguro vêm das viagens do período.
          </p>
          <ul className="flex flex-wrap gap-2">
            {d.sources.map((s) => (
              <li key={s.name} className="rounded-full bg-surface-muted px-2.5 py-1 text-[11px] text-gray-600">
                <span className="font-semibold text-gray-900">{s.name}</span>{" "}
                {s.lastSyncAt ? `sincronizado em ${fmtDateTime(s.lastSyncAt)}` : "sem registro de sincronização"}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
